import { Injectable, signal, computed } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { SettingsService } from './settings.service';

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T | null;
}

export interface ArkifiHubShoppingCardResponseDto {
  cardNumber: string;
  balance: number;
  platformId: string;
  businessId: string;
  environmentType: string;
  dateCreated: string;
  dateUpdated: string;
}

export interface ArkifiHubShoppingCardExistsResponseDto {
  hasCard: boolean;
  cardNumber?: string;
  balance?: number;
}

export interface FundArkifiHubShoppingCardDto {
  amount: number;
  callbackUrl: string;
}

export interface FundArkifiHubShoppingCardResponseDto {
  success: boolean;
  message: string;
  authorizationUrl?: string;
  accessCode?: string;
  reference?: string;
  checkoutUrl?: string;
  transactionReference?: string;
  paymentReference?: string;
  gateway?: string;
}

export interface VerifyArkifiHubCardFundingRequestDto {
  transactionReference: string;
}

export interface VerifyArkifiHubCardFundingResponseDto {
  isSuccessful: boolean;
  message: string;
  transactionReference: string;
  status: string;
  amount: number;
  newBalance?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ShoppingCardService {
  private shoppingCardApiUrl = `${environment.apiUrl}/api/ArkifiHubShoppingCard`;
  private cardFundingApiUrl = `${environment.apiUrl}/api/ArkifiHubCardFunding`;
  
  // Store card state per platform
  private cardCache = new Map<string, ArkifiHubShoppingCardResponseDto | null>();
  
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private settingsService: SettingsService
  ) {}

  private getAuthHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    if (!token) {
      console.warn('No authentication token found. User may need to log in again.');
    }
    if (token) {
      return new HttpHeaders({
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      });
    }
    return new HttpHeaders({
      'Content-Type': 'application/json'
    });
  }

  private getCacheKey(platformId: string, environment: string): string {
    return `${platformId}_${environment}`;
  }

  /**
   * Check if shopping card exists for customer on a platform and environment
   */
  checkShoppingCardExists(platformId: string, environment: 'Live' | 'Test'): Observable<ApiResponse<ArkifiHubShoppingCardExistsResponseDto>> {
    const params = new HttpParams()
      .set('platformId', platformId)
      .set('environment', environment);

    return this.http.get<ApiResponse<ArkifiHubShoppingCardExistsResponseDto>>(
      `${this.shoppingCardApiUrl}/check`,
      {
        headers: this.getAuthHeaders(),
        params: params
      }
    );
  }

  /**
   * Get shopping card for customer on a platform and environment
   */
  getShoppingCard(platformId: string, environment: 'Live' | 'Test'): Observable<ApiResponse<ArkifiHubShoppingCardResponseDto>> {
    const params = new HttpParams()
      .set('platformId', platformId)
      .set('environment', environment);

    const cacheKey = this.getCacheKey(platformId, environment);

    return this.http.get<ApiResponse<ArkifiHubShoppingCardResponseDto>>(
      `${this.shoppingCardApiUrl}/get`,
      {
        headers: this.getAuthHeaders(),
        params: params
      }
    ).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.cardCache.set(cacheKey, response.data);
        } else {
          this.cardCache.set(cacheKey, null);
        }
      })
    );
  }

  /**
   * Generate shopping card for customer on a platform and environment
   */
  generateShoppingCard(platformId: string, environment: 'Live' | 'Test'): Observable<ApiResponse<ArkifiHubShoppingCardResponseDto>> {
    const params = new HttpParams()
      .set('platformId', platformId)
      .set('environment', environment);

    const cacheKey = this.getCacheKey(platformId, environment);

    return this.http.post<ApiResponse<ArkifiHubShoppingCardResponseDto>>(
      `${this.shoppingCardApiUrl}/generate`,
      {},
      {
        headers: this.getAuthHeaders(),
        params: params
      }
    ).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.cardCache.set(cacheKey, response.data);
        }
      })
    );
  }

  /**
   * Fund shopping card for customer
   */
  fundShoppingCard(
    platformId: string,
    environment: 'Live' | 'Test',
    amount: number,
    callbackUrl: string
  ): Observable<ApiResponse<FundArkifiHubShoppingCardResponseDto>> {
    const params = new HttpParams()
      .set('platformId', platformId)
      .set('environment', environment);

    const request: FundArkifiHubShoppingCardDto = {
      amount: amount,
      callbackUrl: callbackUrl
    };

    return this.http.post<ApiResponse<FundArkifiHubShoppingCardResponseDto>>(
      `${this.cardFundingApiUrl}/fund`,
      request,
      {
        headers: this.getAuthHeaders(),
        params: params
      }
    );
  }

  /**
   * Verify card funding transaction
   */
  verifyCardFunding(transactionReference: string): Observable<ApiResponse<VerifyArkifiHubCardFundingResponseDto>> {
    const request: VerifyArkifiHubCardFundingRequestDto = {
      transactionReference: transactionReference
    };

    return this.http.post<ApiResponse<VerifyArkifiHubCardFundingResponseDto>>(
      `${this.cardFundingApiUrl}/verify`,
      request,
      {
        headers: this.getAuthHeaders()
      }
    );
  }

  /**
   * Clear cache for a specific platform/environment
   */
  clearCache(platformId: string, environment: 'Live' | 'Test'): void {
    const cacheKey = this.getCacheKey(platformId, environment);
    this.cardCache.delete(cacheKey);
  }

  /**
   * Get cached card data
   */
  getCachedCard(platformId: string, environment: 'Live' | 'Test'): ArkifiHubShoppingCardResponseDto | null {
    const cacheKey = this.getCacheKey(platformId, environment);
    return this.cardCache.get(cacheKey) || null;
  }
}
